import { type FC, useMemo } from "react";
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Button,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  FormBuilder,
  lockSchema,
  SECRET_PLACEHOLDER,
} from "@/shared/components/Form";
import {
  useCurrentPlatformQuery,
  useConnectPaynetMutation,
  useDisconnectPaynetMutation,
} from "@/partner/entities/platforms";
import {
  interpolationFormat,
  resolveMutationErrorKey,
} from "@/partner/shared/lib";
import { makePaynetSettingsSchema } from "../model/schemas";

/**
 * Коды отказа бекенда (extensions.code) и тексты под них, как у Stripe.
 * Сообщение провайдера наружу не идёт: оно способно вернуть эхом часть
 * переданных секретов.
 */
const CONNECT_ERROR_KEYS: Record<string, string> = {
  INVALID_PAYNET_SETTINGS: "paymentSettings.paynetErrors.invalidSettings",
  INVALID_PAYNET_CREDENTIALS: "paymentSettings.paynetErrors.invalidCredentials",
  PAYNET_API_ERROR: "paymentSettings.paynetErrors.apiError",
};

const CONNECT_FALLBACK_KEY = "paymentSettings.paynetErrors.fallback";
const DISCONNECT_FALLBACK_KEY = "paymentSettings.disconnectError";

/**
 * Блок подключения Paynet — 1:1 по образцу PaymentSettingsCard (Stripe),
 * рядом с которым рендерится (см. PlatformSettings.tsx). Секреты
 * (secretKey/password) наружу не отдаются вообще, даже маской (см. схему
 * PlatformPaynetSettings) — initialValues их не подставляет, а после
 * подключения экран показывает только «подключено с <дата>» и кнопку
 * отключения.
 */
export const PaynetSettingsCard: FC = () => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data, refetch } = useCurrentPlatformQuery();

  const paynet = data?.currentPlatform?.paymentSettings?.paynet;
  // Пока Paynet подключён, поля только показывают сохранённые значения
  // (секреты точками); редактировать можно после «Отключить».
  const locked = Boolean(paynet?.connectedAt);
  const schema = useMemo(() => {
    const base = makePaynetSettingsSchema(t);
    return locked ? lockSchema(base) : base;
  }, [t, locked]);

  const [connectPaynet, { loading: connecting }] = useConnectPaynetMutation({
    onCompleted: () => {
      enqueueSnackbar(t("paymentSettings.paynetConnectedSnackbar"), {
        variant: "success",
      });
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(
        t(
          resolveMutationErrorKey(
            error,
            CONNECT_ERROR_KEYS,
            CONNECT_FALLBACK_KEY
          )
        ),
        { variant: "error" }
      );
    },
  });

  const [disconnectPaynet, { loading: disconnecting }] =
    useDisconnectPaynetMutation({
      onCompleted: () => {
        enqueueSnackbar(t("paymentSettings.paynetDisconnectedSnackbar"), {
          variant: "success",
        });
        refetch();
      },
      onError: (error) =>
        enqueueSnackbar(
          t(resolveMutationErrorKey(error, {}, DISCONNECT_FALLBACK_KEY)),
          {
            variant: "error",
          }
        ),
    });

  return (
    <Card sx={{ mt: 2 }}>
      {(connecting || disconnecting) && <LinearProgress />}
      <CardHeader
        title={t("paymentSettings.paynetTitle")}
        action={
          paynet?.connectedAt ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                color="success"
                label={t("paymentSettings.paynetConnectedAt", {
                  date: interpolationFormat(
                    paynet.connectedAt,
                    "dateTime",
                    i18n.language
                  ),
                })}
              />
              <Chip
                size="small"
                variant="outlined"
                label={paynet.merchantCode}
              />
            </Stack>
          ) : (
            <Chip
              size="small"
              label={t("streamingSettings.status.notConnected")}
            />
          )
        }
      />
      <CardContent>
        {connecting && (
          <Alert
            severity="info"
            icon={<CircularProgress size={16} />}
            sx={{ mb: 2 }}
          >
            {t("paymentSettings.paynetConnecting")}
          </Alert>
        )}
        <FormBuilder
          key={locked ? "locked" : "editable"}
          formId="paynet-settings-form"
          schema={schema}
          initialValues={{
            merchantCode: paynet?.merchantCode ?? "",
            secretKey: locked ? SECRET_PLACEHOLDER : "",
            user: locked ? SECRET_PLACEHOLDER : "",
            password: locked ? SECRET_PLACEHOLDER : "",
            saleArea: paynet?.saleArea ?? "",
          }}
          onSubmit={async (values) => {
            await connectPaynet({
              variables: {
                input: {
                  merchantCode: String(values.merchantCode),
                  secretKey: String(values.secretKey),
                  user: String(values.user),
                  password: String(values.password),
                  saleArea: values.saleArea
                    ? String(values.saleArea)
                    : undefined,
                },
              },
            });
          }}
          submitButton={!locked}
          resetButton={false}
          submitButtonText={t("paymentSettings.connectPaynetButton")}
          disabled={connecting || locked}
          actionsAlign="end"
        />
        {paynet?.connectedAt && (
          <>
            {disconnecting && (
              <Alert
                severity="info"
                icon={<CircularProgress size={16} />}
                sx={{ mt: 2 }}
              >
                {t("paymentSettings.paynetDisconnecting")}
              </Alert>
            )}
            <Button
              variant="outlined"
              color="error"
              sx={{ mt: 2 }}
              disabled={disconnecting}
              onClick={() => disconnectPaynet()}
            >
              {t("paymentSettings.disconnectPaynetButton")}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};
